import { useCallback, useEffect, useState } from "react";
import { fetchFavourites, type FavouriteCar } from "@/lib/users-api";

type SavedState = {
  token: string | null;
  status: "loading" | "ready";
  cars: FavouriteCar[];
};

/** Keep both pending and completed reads bound to the current session. */
export function useSavedCars(token: string | null) {
  const [state, setState] = useState<SavedState>({ token: null, status: "ready", cars: [] });
  useEffect(() => {
    let current = true;
    if (!token) {
      setState({ token: null, status: "ready", cars: [] });
      return;
    }
    setState({ token, status: "loading", cars: [] });
    fetchFavourites(token).then(
      cars => { if (current) setState({ token, status: "ready", cars }); },
      error => {
        if (!current) return;
        console.warn("Failed to fetch favourites:", error);
        setState({ token, status: "ready", cars: [] });
      },
    );
    return () => { current = false; };
  }, [token]);

  const scoped = state.token === token ? state : null;
  const setCars = useCallback((update: (cars: FavouriteCar[]) => FavouriteCar[]) => {
    setState(previous => previous.token === token
      ? { ...previous, cars: update(previous.cars) }
      : previous);
  }, [token]);
  return {
    cars: scoped?.cars ?? [],
    loading: Boolean(token && (!scoped || scoped.status === "loading")),
    setCars,
  };
}
